import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal, Plus, Stethoscope } from 'lucide-react';
import { useState } from 'react';
import z from 'zod';

import type {
  GetSpecialities,
  getSpecialitiesSchema,
} from '@virtual-care/client';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@virtual-care/shadcn';
import {
  ConfirmDialog,
  SearchInput,
  TableRowSort,
  client,
  useAction,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import { CreateSpeciality } from './create-speciality.tsx';
import { UpdateSpecialityDialog } from './update-speciality.tsx';

export default function SpecialitiesPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateSpeciality, setUpdateSpeciality] = useState<
    GetSpecialities['records'][number] | null
  >(null);
  const deleteSpecialityMutation = useAction('DELETE /specialities/{id}');

  const columns: ColumnDef<GetSpecialities['records'][0]>[] = [
    {
      accessorKey: 'name',
      header: 'Arabic Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },
    {
      accessorKey: 'nameEn',
      header: 'English Name',
      cell: ({ row }) => {
        const nameEn = row.getValue<string | null>('nameEn');
        return nameEn ? (
          <div className="font-medium">{nameEn}</div>
        ) : (
          <span className="text-muted-foreground">Not set</span>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Arabic Description',
      cell: ({ row }) => (
        <div
          className="max-w-[500px] truncate"
          title={row.getValue('description')}
        >
          {row.getValue('description')}
        </div>
      ),
    },
    {
      accessorKey: 'descriptionEn',
      header: 'English Description',
      cell: ({ row }) => {
        const descriptionEn = row.getValue<string | null>('descriptionEn');
        return descriptionEn ? (
          <div className="max-w-[500px] truncate" title={descriptionEn}>
            {descriptionEn}
          </div>
        ) : (
          <span className="text-muted-foreground">Not set</span>
        );
      },
    },
    {
      accessorKey: 'insuranceCode',
      header: 'Insurance Code',
      cell: ({ row }) => (
        <Badge variant="outline">{row.getValue('insuranceCode')}</Badge>
      ),
    },
    {
      id: 'doctorCount',
      header: 'Doctors',
      cell: ({ row }) => {
        return <Badge variant="secondary">{row.original._count.doctors}</Badge>;
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const speciality = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(speciality.id)}
              >
                Copy speciality ID
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <UpdateSpecialityDialog
                onOpenChange={setUpdateDialogOpen}
                open={updateDialogOpen}
                speciality={speciality}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    Edit speciality
                  </DropdownMenuItem>
                }
              />
              <ConfirmDialog
                title="Delete Speciality"
                description={
                  <span>
                    Are you sure you want to delete speciality{' '}
                    <span className="font-bold">{speciality.name}</span>? This
                    action cannot be undone.
                  </span>
                }
                onConfirm={async () => {
                  await deleteSpecialityMutation.mutateAsync({
                    id: speciality.id,
                  });
                  await dataQuery.refetch();
                }}
              >
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  Delete speciality
                </DropdownMenuItem>
              </ConfirmDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useQuery({
    queryKey: ['specialities', pagination, sorting, searchQuery],
    queryFn: () => {
      const [sort] = sorting;
      return client.request('GET /specialities', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        query: searchQuery || undefined,
        sortBy: sort?.id as z.infer<typeof getSpecialitiesSchema.shape.sortBy>,
        sortOrder: sort?.desc ? 'desc' : 'asc',
      });
    },
    placeholderData: keepPreviousData,
  });

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns: columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount || 0,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="Specialities"
        totalCount={`${dataQuery.data?.totalCount || 0} total specialities`}
        icon={Stethoscope}
      >
        <CreateSpeciality
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          trigger={
            <Button>
              <Plus className="mr-2 size-4" />
              Add Speciality
            </Button>
          }
          onSave={() => dataQuery.refetch()}
        />
      </CountablePageHeader>

      {updateSpeciality && (
        <UpdateSpecialityDialog
          speciality={updateSpeciality}
          trigger={null}
          open={!!updateSpeciality}
          onOpenChange={(open) => {
            if (!open) {
              setUpdateSpeciality(null);
            }
          }}
        />
      )}

      <div className="mb-4">
        <SearchInput
          placeholder="Search specialities..."
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No specialities found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}
